<script setup lang="ts">
/**
 * Today's quests: what each one asks for, how far along it is, and what it pays.
 *
 * ── Where the list comes from ────────────────────────────────────────────────
 *
 * The server, entirely. Which four quests a player has today is derived from the
 * day and their account id — there is no scheduler that deals them out — and the
 * wording arrives with the featured hull, turret or map already substituted. So
 * this file holds **no copy of the quest pool**: not the targets, not the
 * rewards, not the names. Same rule the research tree follows for its branches
 * and the ladder for its ranks. An eleventh quest is an entry in
 * `quests/daily-quests.ts` on the server and nothing at all here.
 *
 * ── The countdown re-reads rather than concluding ────────────────────────────
 *
 * `secondsUntilReset` is a remainder measured at the instant of the response,
 * exactly like `secondsToNextFuel` and a research's `secondsRemaining`. It is
 * counted down locally, and when it reaches zero the window **asks again**
 * instead of deciding for itself that a new day has begun and clearing the
 * progress on screen. Whether the day has changed is the server's answer.
 *
 * ── Claiming is a courtesy here and a guard there ────────────────────────────
 *
 * The Claim button is disabled for a quest that is unfinished or already paid,
 * and that is decoration. The real single-use guard is the conditional write on
 * the server: a claim matches on `claimedAt: null` inside the transaction that
 * grants the money, so a double click, two tabs and two racing requests all end
 * with one payment. Nothing here may ever become where that is enforced.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import type { DailyQuest, DailyQuests } from '@/net/api';
import { formatCredits } from './stat-display';
import { playUiConfirm, playUiHover } from '@/ui/ui-sound';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';

const props = defineProps<{
  quests: DailyQuests;
  /** Which quest is mid-request, so its button can say so. Null for none. */
  busyId: string | null;
  /** When the overview these figures came from arrived. The countdown measures from here. */
  loadedAtMs: number;
  /** The player's diamonds, to disable a re-roll they cannot pay for. */
  diamonds: number;
}>();

const emit = defineEmits<{
  close: [];
  claim: [questId: string];
  /** Asked for a re-roll; the owner confirms the spend. */
  reroll: [];
  /**
   * The day rolled over while the window was open.
   *
   * Emitted rather than handled, because re-reading the garage belongs to
   * whoever owns the store. The window never invents a new day for itself.
   */
  elapsed: [];
}>();

const dialog = ref<HTMLElement | null>(null);

/**
 * A local clock, ticking once a second, purely so the countdown moves.
 *
 * It measures against `loadedAtMs` rather than accumulating, so a tab that was
 * suspended for an hour comes back with the right remainder instead of an hour
 * of missed intervals.
 */
const nowMs = ref(Date.now());
let ticker: ReturnType<typeof setInterval> | null = null;
let announcedElapsed = false;

const secondsLeft = computed(() => {
  if (!props.loadedAtMs) return props.quests.secondsUntilReset;
  const elapsed = Math.floor((nowMs.value - props.loadedAtMs) / 1000);
  return Math.max(0, props.quests.secondsUntilReset - elapsed);
});

const resetLabel = computed(() => {
  const total = secondsLeft.value;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${total}s`;
});

const claimable = computed(() => props.quests.quests.filter((q) => q.claimable).length);

onMounted(() => {
  dialog.value?.focus();
  ticker = setInterval(() => {
    nowMs.value = Date.now();
    // Once, on the boundary. Repeating it would turn a rollover into a poll.
    if (secondsLeft.value === 0 && !announcedElapsed) {
      announcedElapsed = true;
      emit('elapsed');
    }
  }, 1000);
});

onBeforeUnmount(() => {
  if (ticker) clearInterval(ticker);
});

function percent(quest: DailyQuest): number {
  if (quest.target <= 0) return 0;
  return Math.min(100, Math.round((quest.progress / quest.target) * 100));
}

/**
 * The line under a quest that explains why it is not moving, or null.
 *
 * Two honest reasons, and both are worth stating: a quest that only counts in
 * team battle, and a featured vehicle the player does not own. Leaving either
 * unsaid means somebody watches a counter stay at zero and concludes the feature
 * is broken.
 */
function requirement(quest: DailyQuest): string | null {
  if (quest.subjectOwned === false && quest.subjectName) {
    return `You do not own the ${quest.subjectName} yet.`;
  }
  if (quest.modeName) return `${quest.modeName} only.`;
  return null;
}

/**
 * Close and claim both confirm with a sound before the emit, not after — the
 * claim has a network round trip ahead of it, and a blip that waited for the
 * server would land late enough to read as the *response* rather than the
 * tap. The window's own guards (`busyId`, `disabled`) are what stop a second
 * claim; this only ever plays alongside a click that was already going to
 * fire.
 */
function close(): void {
  playUiConfirm();
  emit('close');
}

function claim(questId: string): void {
  playUiConfirm();
  emit('claim', questId);
}
</script>

<template>
  <div class="scrim" data-test="daily-quests" @click.self="close">
    <section
      ref="dialog"
      class="window"
      role="dialog"
      aria-modal="true"
      aria-label="Daily quests"
      tabindex="-1"
      @keydown.esc="close"
    >
      <header>
        <div>
          <h2>Daily quests</h2>
          <p class="standing">
            <template v-if="claimable > 0">
              <strong data-test="quests-ready">{{ claimable }} ready to claim</strong>
              <span class="muted">· new set in {{ resetLabel }}</span>
            </template>
            <template v-else>
              <span class="muted" data-test="quests-reset">New set in {{ resetLabel }}</span>
            </template>
          </p>
        </div>
        <button
          type="button"
          class="close"
          data-test="daily-quests-close"
          @mouseenter="playUiHover()"
          @click="close"
        >
          Close
        </button>
      </header>

      <ol class="quests">
        <li
          v-for="quest in quests.quests"
          :key="quest.id"
          class="quest"
          :class="{ done: quest.claimed, ready: quest.claimable }"
          :data-test="`quest-${quest.id}`"
          @mouseenter="playUiHover()"
        >
          <div class="head">
            <span class="name">{{ quest.name }}</span>
            <span class="reward" :data-test="`quest-reward-${quest.id}`">
              <span class="credits">
                {{ formatCredits(quest.reward.credits) }} <CurrencyIcon kind="credits" />
              </span>
              <span class="xp">{{ formatCredits(quest.reward.xp) }} xp</span>
            </span>
          </div>

          <p class="blurb">{{ quest.blurb }}</p>
          <p v-if="requirement(quest)" class="requirement">{{ requirement(quest) }}</p>

          <div class="progress">
            <div class="track"><i :style="{ width: `${percent(quest)}%` }"></i></div>
            <span class="figures" :data-test="`quest-progress-${quest.id}`">
              {{ formatCredits(quest.progress) }} / {{ formatCredits(quest.target) }}
            </span>
          </div>

          <div class="actions">
            <span v-if="quest.claimed" class="claimed" :data-test="`quest-claimed-${quest.id}`">
              Claimed
            </span>
            <button
              v-else
              type="button"
              class="claim"
              :data-test="`quest-claim-${quest.id}`"
              :disabled="!quest.claimable || busyId !== null"
              @mouseenter="quest.claimable && busyId === null && playUiHover()"
              @click="claim(quest.id)"
            >
              {{ quest.claimable ? 'Claim' : 'In progress' }}
            </button>
          </div>
        </li>
      </ol>

      <div class="reroll" data-test="quests-reroll-row">
        <span class="reroll-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M20 12a8 8 0 1 1-2.4-5.7" /><path d="M20 3.5V8h-4.5" /></svg>
        </span>
        <span class="reroll-copy">
          <strong>Re-roll missions</strong>
          <span class="reroll-hint">
            <template v-if="!quests.rerollable">Every quest is finished.</template>
            <template v-else-if="diamonds < quests.rerollCost">Not enough diamonds.</template>
            <template v-else>Swaps the unfinished ones. Finished quests stay.</template>
          </span>
        </span>
        <button
          type="button"
          class="reroll-btn"
          data-test="quests-reroll"
          :disabled="!quests.rerollable || busyId !== null || diamonds < quests.rerollCost"
          @mouseenter="playUiHover()"
          @click="emit('reroll')"
        >
          Re-roll
          <span class="reroll-price">{{ quests.rerollCost }} <CurrencyIcon kind="diamonds" :labelled="false" /></span>
        </button>
      </div>

      <footer>
        <span class="muted">
          A full day is worth {{ formatCredits(quests.dayReward.credits) }}
          <CurrencyIcon kind="credits" /> and
          {{ formatCredits(quests.dayReward.xp) }} xp.
        </span>
      </footer>
    </section>
  </div>
</template>

<style scoped>
/* The same scrim and window the rank ladder uses — a thing you look up and
   close again, not a place to be. */
.scrim {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(6, 8, 11, 0.72);
  z-index: 40;
}

.window {
  width: min(34rem, 94vw);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 0.5rem;
  background: #12151a;
  overflow: hidden;
}

header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1.2rem 0.9rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

h2 {
  margin: 0;
  font-size: 1.05rem;
}

.standing {
  margin: 0.3rem 0 0;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.82rem;
}

.standing strong {
  color: var(--accent);
}

.muted {
  color: var(--steel-400);
  font-size: 0.78rem;
}

.close {
  flex: none;
  padding: 0.35rem 0.8rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: transparent;
  color: var(--steel-200, #d7dde3);
  font-size: 0.75rem;
  transition:
    border-color 120ms ease,
    background 120ms ease,
    color 120ms ease;
}

.close:hover {
  border-color: var(--steel-400);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.quests {
  margin: 0;
  padding: 0.8rem 1.2rem;
  list-style: none;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.quest {
  padding: 0.7rem 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.02);
  transition:
    border-color 120ms ease,
    background 120ms ease;
}

/* A plain brighten rather than a colour, so it layers under `.ready`'s accent
   wash and `.done`'s dimming instead of fighting either — this row is not
   itself a control (the claim button is), so the hover says "this is the one
   under your cursor" rather than "click me". */
.quest:hover {
  border-color: rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.05);
}

.quest.ready:hover {
  background: linear-gradient(
    180deg,
    rgba(232, 114, 44, 0.22) 0%,
    rgba(232, 114, 44, 0.1) 100%
  );
}

/* Finished and unpaid is the one state worth shouting about — it is the reason
   somebody opened this window. The border and the doubling ring were already
   here and are unchanged; the wash is what was added, so that a claimable quest
   looks the same on this row, on the garage floor's own card and on the profile
   overlay's selected chip. Three surfaces, one way of saying "this one". */
.quest.ready {
  border-color: var(--accent);
  box-shadow: inset 0 0 0 1px var(--accent);
  background: linear-gradient(
    180deg,
    rgba(232, 114, 44, 0.16) 0%,
    rgba(232, 114, 44, 0.07) 100%
  );
}

.quest.done {
  opacity: 0.55;
}

.head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
}

.name {
  font-size: 0.95rem;
  font-weight: 600;
}

.reward {
  flex: none;
  display: flex;
  gap: 0.5rem;
  font-variant-numeric: tabular-nums;
  font-size: 0.78rem;
}

.reward .credits {
  color: var(--accent);
  font-weight: 600;
}

.reward .xp {
  color: var(--steel-400);
}

.blurb {
  margin: 0.25rem 0 0;
  color: var(--steel-400);
  font-size: 0.78rem;
  line-height: 1.4;
}

/* A local literal rather than a palette token, as `GarageLobby` does with its
   destructive red: the palette has no "attention" colour and inventing one here
   would commit every future amber in the app to this shade. */
.requirement {
  margin: 0.25rem 0 0;
  color: #d9a441;
  font-size: 0.74rem;
}

.progress {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.55rem;
}

.track {
  flex: 1;
  height: 0.35rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.09);
  overflow: hidden;
}

.track i {
  display: block;
  height: 100%;
  background: var(--accent);
}

.figures {
  flex: none;
  color: var(--steel-400);
  font-size: 0.74rem;
  font-variant-numeric: tabular-nums;
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

.claim {
  padding: 0.35rem 1.1rem;
  border: 1px solid var(--accent);
  border-radius: 3px;
  background: var(--accent);
  color: #14181d;
  font-size: 0.76rem;
  font-weight: 700;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    transform 120ms ease;
}

.claim:hover:not(:disabled) {
  background: #f0834a;
  border-color: #f0834a;
}

.claim:active:not(:disabled) {
  transform: scale(0.96);
}

.claim:disabled {
  border-color: var(--steel-600);
  background: transparent;
  color: var(--steel-400);
  font-weight: 500;
}

.claimed {
  color: var(--steel-400);
  font-size: 0.76rem;
}

footer {
  padding: 0.7rem 1.2rem 0.9rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

/* The colour transitions above stay — they are a state change, not motion.
   Only the claim button's press-down scale is movement, so it is the only
   thing this drops. */
@media (prefers-reduced-motion: reduce) {
  .claim {
    transition:
      background 120ms ease,
      border-color 120ms ease;
  }

  .claim:active:not(:disabled) {
    transform: none;
  }
}

.reroll {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0 1.2rem 0.8rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.04);
}

.reroll-icon {
  flex: none;
  display: grid;
  place-items: center;
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 50%;
  background: rgba(232, 114, 44, 0.14);
  color: var(--accent);
}

.reroll-icon svg {
  width: 1.15rem;
  height: 1.15rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.reroll-copy {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.reroll-copy strong {
  color: #eef2f7;
  font-size: 0.9rem;
}

.reroll-hint {
  color: var(--steel-400);
  font-size: 0.74rem;
}

.reroll-btn {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  min-height: 2.75rem;
  padding: 0.35rem 0.5rem 0.35rem 0.9rem;
  border: 1px solid var(--accent);
  border-radius: 3px;
  background: rgba(232, 114, 44, 0.12);
  color: #fff;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.reroll-price {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.4);
  color: #bfe9ff;
  font-variant-numeric: tabular-nums;
}

.reroll-btn:hover:not(:disabled) {
  background: var(--accent);
  color: #0b0d11;
}

.reroll-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
